import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldAlert, MailCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { useToast } from "@/hooks/use-toast";
import {
  useVerifyEmail,
  useResendVerification,
  type VerifyEmailStatus
} from "@/hooks/useEmailVerification";

type Phase = "checking" | "success" | "expired" | "invalid";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { toast } = useToast();

  const verify = useVerifyEmail();
  const resend = useResendVerification();

  const [phase, setPhase] = React.useState<Phase>("checking");
  const [resendEmail, setResendEmail] = React.useState("");
  const ran = React.useRef(false);

  React.useEffect(() => {
    if (ran.current) return; // guard React 18 StrictMode double-invoke
    ran.current = true;

    if (!token) {
      setPhase("invalid");
      return;
    }

    verify
      .mutateAsync(token)
      .then((res) => {
        const status: VerifyEmailStatus = res.status;
        if (status === "SUCCESS" || status === "ALREADY_VERIFIED") setPhase("success");
        else if (status === "TOKEN_EXPIRED") setPhase("expired");
        else setPhase("invalid");
      })
      .catch(() => setPhase("invalid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onResend(e: React.FormEvent) {
    e.preventDefault();
    if (resend.isPending || !resendEmail) return;
    try {
      await resend.mutateAsync(resendEmail);
      toast({
        title: "Check your inbox",
        description: "If your account needs verification, a new link is on its way."
      });
    } catch {
      toast({
        title: "Couldn't resend",
        description: "Please try again in a moment.",
        variant: "destructive"
      });
    }
  }

  return (
    <AuthLayout
      eyebrow="Account security"
      title="Verify your email"
      sub="Confirming your email keeps your account secure and unlocks checkout."
    >
      <Card className="border-brand-border shadow-brand-md">
        {phase === "checking" && (
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <CardDescription>Verifying your email…</CardDescription>
          </CardContent>
        )}

        {phase === "success" && (
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <CardTitle className="text-xl">Email verified</CardTitle>
            <CardDescription className="max-w-xs">
              You're all set. Your account is verified and ready to go.
            </CardDescription>
            <Button className="mt-2" onClick={() => navigate("/dashboard", { replace: true })}>
              Go to dashboard
            </Button>
          </CardContent>
        )}

        {(phase === "expired" || phase === "invalid") && (
          <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl">
                {phase === "expired" ? "Link expired" : "Link invalid"}
              </CardTitle>
              <CardDescription className="mx-auto mt-1 max-w-xs">
                {phase === "expired"
                  ? "This verification link has expired. Request a fresh one below."
                  : "This verification link is invalid. Request a fresh one below."}
              </CardDescription>
            </div>
            <form onSubmit={onResend} className="w-full max-w-xs space-y-3">
              <Input
                type="email"
                required
                placeholder="you@example.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
              />
              <Button type="submit" disabled={resend.isPending} className="w-full">
                {resend.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MailCheck className="h-4 w-4" />
                )}
                Send a new link
              </Button>
            </form>
          </CardContent>
        )}
      </Card>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Back to{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
